import { useCallback, useEffect, useMemo, useRef, useState, type SyntheticEvent } from 'react';
import type { Piece } from '../queries/edit';
import {
  nearestOutput,
  pieceId,
  pieceLength,
  placedAtOut,
  plan as planPieces,
  plannedDuration,
  toOutput,
  type Placed,
} from '../lib/timeline';

/**
 * Playing an edit before it has rendered, piece by piece.
 *
 * The original plays from storage in one element; each clip plays from its
 * own recording in a second element laid over it. Time here is a second in
 * the finished video: the hook keeps the piece under the playhead, moves
 * the right element to the right second inside it, and steps to the next
 * piece when the current one runs out, in list order, whatever the order.
 * The editor and the share page's preview both play through this, so what
 * the editor shows is what the page will.
 */

export interface PlanPlayer {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  clipVideoRef: React.RefObject<HTMLVideoElement | null>;
  /** The placed pieces, as they stand. */
  plan: Placed[];
  total: number;
  /** A second in the finished video. */
  outTime: number;
  /** A second of the original: where the playhead is when on the original, else where it last was. */
  sourceTime: number;
  playing: boolean;
  /** The piece under the playhead. */
  current: Placed | null;
  /** The clip piece showing, if the playhead is inside one. */
  activeClip: Placed | null;
  /** A second inside the showing clip's source. */
  clipTime: number;
  toggle: () => void;
  seekOut: (out: number) => void;
  seekSource: (sourceTime: number) => void;
  /** Handlers for the clip element; the caller renders it keyed by the clip's id. */
  clipHandlers: {
    onLoadedMetadata: (event: SyntheticEvent<HTMLVideoElement>) => void;
    onTimeUpdate: (event: SyntheticEvent<HTMLVideoElement>) => void;
    onEnded: () => void;
    onError: () => void;
    onPlay: () => void;
    onPause: () => void;
  };
}

const EDGE = 0.03;

export function usePlanPlayer({ pieces }: { pieces: Piece[] }): PlanPlayer {
  const plan = useMemo(() => planPieces(pieces), [pieces]);
  const total = plannedDuration(plan);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const clipVideoRef = useRef<HTMLVideoElement | null>(null);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [outTime, setOutTime] = useState(0);
  const [sourceTime, setSourceTime] = useState(pieces.find((p) => p.kind === 'self')?.start ?? 0);
  const [clipTime, setClipTime] = useState(0);
  const [playing, setPlaying] = useState(false);

  // Whether the person wants it running, whichever element does the work: a
  // clip that mounts during a paused scrub must not start on its own, and
  // the pause the original takes while a clip plays is not one they asked for.
  const wantPlay = useRef(false);
  const handingOver = useRef(false);
  const planRef = useRef(plan);
  planRef.current = plan;
  const activeIdRef = useRef<string | null>(null);
  activeIdRef.current = activeId;

  /** The piece under the playhead: the one being played, else the one at the clock. */
  const current = useMemo<Placed | null>(() => {
    const byId = activeId ? plan.find((one) => pieceId(one.piece) === activeId) : undefined;
    return byId ?? placedAtOut(plan, outTime);
  }, [activeId, outTime, plan]);
  const activeClip = current && current.piece.kind === 'clip' ? current : null;
  // Available to handlers without being rebuilt per frame.
  const currentRef = useRef(current);
  currentRef.current = current;

  const enter = useCallback((placed: Placed, sourceAt: number) => {
    const at = Math.max(placed.piece.start, Math.min(sourceAt, placed.piece.end));
    setActiveId(pieceId(placed.piece));
    setOutTime(placed.outStart + (at - placed.piece.start));
    const video = videoRef.current;
    if (placed.piece.kind === 'self') {
      setSourceTime(at);
      if (!video) return;
      video.currentTime = at;
      if (wantPlay.current) void video.play().catch(() => {});
      return;
    }
    // A clip takes over: the original waits, without that reading as a pause.
    if (video && !video.paused) {
      handingOver.current = true;
      video.pause();
    }
    setClipTime(at);
  }, []);

  const finish = useCallback(() => {
    wantPlay.current = false;
    setPlaying(false);
    const video = videoRef.current;
    if (video && !video.paused) video.pause();
    setOutTime(plannedDuration(planRef.current));
  }, []);

  /** The current piece has run out: the next one, or the end. */
  const advance = useCallback(() => {
    const now = currentRef.current;
    const next = now ? planRef.current[now.index + 1] : planRef.current[0];
    if (!next) {
      finish();
      return;
    }
    enter(next, next.piece.start);
  }, [enter, finish]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onTime = () => {
      const placed = currentRef.current;
      if (!placed || placed.piece.kind !== 'self') return;
      const now = video.currentTime;
      if (now >= placed.piece.end - EDGE) {
        if (!video.paused || wantPlay.current) advance();
        else {
          setSourceTime(placed.piece.end);
          setOutTime(placed.outEnd);
        }
        return;
      }
      setSourceTime(now);
      setOutTime(placed.outStart + Math.max(0, now - placed.piece.start));
    };
    const onEnded = () => advance();
    const onPlay = () => {
      wantPlay.current = true;
      setPlaying(true);
    };
    const onPause = () => {
      if (handingOver.current) {
        handingOver.current = false;
        return;
      }
      wantPlay.current = false;
      setPlaying(false);
    };
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('ended', onEnded);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    return () => {
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
    };
  }, [advance]);

  // The list changed under the playhead (a trim, a reorder, a deletion):
  // if the piece being played is gone, the clock decides which piece it is on.
  useEffect(() => {
    if (activeId && !plan.some((one) => pieceId(one.piece) === activeId)) {
      const placed = placedAtOut(plan, Math.min(outTime, plannedDuration(plan)));
      if (placed) enter(placed, placed.piece.start + Math.max(0, Math.min(outTime, plannedDuration(plan)) - placed.outStart));
      else setActiveId(null);
    }
  }, [activeId, enter, outTime, plan]);

  const seekOut = useCallback(
    (out: number) => {
      const placed = placedAtOut(planRef.current, Math.max(0, Math.min(out, plannedDuration(planRef.current))));
      if (!placed) return;
      const offset = Math.max(0, Math.min(out, plannedDuration(planRef.current))) - placed.outStart;
      if (placed.piece.kind === 'clip' && activeIdRef.current === pieceId(placed.piece) && clipVideoRef.current) {
        // Inside the clip already showing: its element stays mounted, so it
        // is moved by hand rather than through a fresh load.
        const at = placed.piece.start + offset;
        clipVideoRef.current.currentTime = at;
        setClipTime(at);
        setOutTime(placed.outStart + offset);
        return;
      }
      enter(placed, placed.piece.start + offset);
    },
    [enter],
  );

  const seekSource = useCallback(
    (at: number) => {
      const out = toOutput(at, planRef.current) ?? nearestOutput(at, planRef.current);
      seekOut(out);
    },
    [seekOut],
  );

  const toggle = useCallback(() => {
    const placed = currentRef.current;
    const clipVideo = clipVideoRef.current;
    if (placed?.piece.kind === 'clip' && clipVideo) {
      if (clipVideo.paused) {
        wantPlay.current = true;
        void clipVideo.play().catch(() => {});
      } else {
        wantPlay.current = false;
        clipVideo.pause();
      }
      return;
    }
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      const end = plannedDuration(planRef.current);
      if (outTime >= end - EDGE && planRef.current[0]) {
        // Again from the top.
        wantPlay.current = true;
        enter(planRef.current[0], planRef.current[0].piece.start);
        return;
      }
      if (!placed) {
        const first = planRef.current[0];
        if (first) {
          wantPlay.current = true;
          enter(first, first.piece.start);
          return;
        }
      }
      wantPlay.current = true;
      void video.play().catch(() => {});
    } else {
      wantPlay.current = false;
      video.pause();
    }
  }, [enter, outTime]);

  const clipHandlers = useMemo<PlanPlayer['clipHandlers']>(
    () => ({
      onLoadedMetadata: (event) => {
        const placed = currentRef.current;
        if (!placed || placed.piece.kind !== 'clip') return;
        // Wherever the clock says inside the clip, not always its in point.
        const into = Math.max(0, outTime - placed.outStart);
        event.currentTarget.currentTime = placed.piece.start + Math.min(into, pieceLength(placed.piece));
        if (wantPlay.current) void event.currentTarget.play().catch(() => {});
      },
      onTimeUpdate: (event) => {
        const placed = currentRef.current;
        if (!placed || placed.piece.kind !== 'clip') return;
        const now = event.currentTarget.currentTime;
        setClipTime(now);
        setOutTime(placed.outStart + Math.max(0, Math.min(now - placed.piece.start, pieceLength(placed.piece))));
        if (now >= placed.piece.end - EDGE && (!event.currentTarget.paused || wantPlay.current)) advance();
      },
      onEnded: () => advance(),
      // A clip that will not play is not worth stranding the preview on.
      onError: () => advance(),
      onPlay: () => {
        wantPlay.current = true;
        setPlaying(true);
      },
      onPause: () => {
        // Running out is followed straight away by the next piece; only a
        // pause somebody asked for shows as one.
        if (!wantPlay.current) setPlaying(false);
      },
    }),
    // outTime is read on mount of a clip element only; rebuilding per frame is harmless.
    [advance, outTime],
  );

  return {
    videoRef,
    clipVideoRef,
    plan,
    total,
    outTime: Math.min(outTime, total),
    sourceTime,
    playing,
    current,
    activeClip,
    clipTime,
    toggle,
    seekOut,
    seekSource,
    clipHandlers,
  };
}
